import i18nFactory from '@yandex-int/i18n';
import { assign } from 'lodash';
import createReReSelector from 're-reselect';
import { createSelector } from 'reselect';

import { GridActionsMenuItems } from '../../components/GridActionsMenu/GridActionsMenu';
import { PricePackagesMenuActionsContainerProps } from '../../containers/PricePackagesMenuActionsContainer/PricePackagesMenuActionsContainer';
import { getPricePackagesFilterCriteriaContainers } from '../../declarations/filter/filter';
import { getPricePackagesColumns } from '../../declarations/pricePackages/pricePackages';
import { PricePackagesHelpers } from '../../service/PricePackagesHelpers/PricePackagesHelpers';
import { AccessRights, ColumnsSettingsViewDict } from '../../typings/common';
import { CriteriaContainer, FilterPartialExpression } from '../../typings/filter';
import { GdOperatorAction, GdStatusApprove } from '../../typings/graphql-grid';
import { pricePackagesAction } from '../../typings/grid';
import {
    PricePackageBaseFields,
    PricePackageEditData,
    PricePackageId,
    PricePackagesClientsModalState,
    PricePackagesMenuActions,
    PricePackagesScreenUrlSearchParams,
    PricePackagesSourceParams
} from '../../typings/pricePackages';
import { State } from '../../typings/state';
import { TagState, TargetingTagType } from '../../typings/tagCloudTargeting';

import * as keyset from './pricePackages.i18n';

const i18n = i18nFactory(keyset);

export const selectEditPricePackage = (state: State, id: PricePackageId): PricePackageEditData => {
    return state.editPricePackages.pricePackages[id];
};

export const selectPricePackagesSourceParams = createSelector(
    (state: State) => {
        const { sourceParams } = state.pricePackages;
        return Object.assign({}, sourceParams);
    },
    (res: PricePackagesSourceParams): PricePackagesSourceParams => res
);

export const selectPricePackagesFilterExpressions = createSelector(
    selectPricePackagesSourceParams,
    (sourceParams: PricePackagesSourceParams): FilterPartialExpression[] => {
        if (!sourceParams || !sourceParams.filter) {
            return [];
        }

        return sourceParams.filter;
    }
);

export const selectPricePackagesFilterContainers = createSelector(
    selectPricePackagesSourceParams,
    (params: PricePackagesSourceParams): CriteriaContainer[] => {
        if (!params) {
            return [];
        }

        return getPricePackagesFilterCriteriaContainers();
    }
);

export const selectPricePackagesColumns = createSelector(
    (state: State) => state.pricePackages.columnsSettings,
    (columnsSettings: ColumnsSettingsViewDict) => {
        return getPricePackagesColumns(columnsSettings);
    }
);

export const selectNormalizedPricePackagesSourceParams = createSelector(
    (state: State) => state.commonData.login,
    (state: State) => state.router.search,
    (login: string, search: string) => {
        const params = new URLSearchParams(search);

        return PricePackagesHelpers.normalizeSourceParams({
            sortKey: params.get(PricePackagesScreenUrlSearchParams.sortKey) || '',
            sortDirection: params.get(PricePackagesScreenUrlSearchParams.sortDirection) || '',
            login: login || params.get(PricePackagesScreenUrlSearchParams.ulogin) || '',
            filter: params.get(PricePackagesScreenUrlSearchParams.filter) || '',
            noStats: params.get(PricePackagesScreenUrlSearchParams.noStats) || ''
        });
    }
);

export const pricePackagesMenuActionsSelector = createReReSelector(
    (state: State, props: PricePackagesMenuActionsContainerProps) => String(props.index),
    (state: State) => state.pricePackages.gridData.total,
    (state: State, props: PricePackagesMenuActionsContainerProps) => props.actions,
    (state: State) => state.commonData.accessRights,
    (state: State) => state.commonData.login,
    (state: State, props: PricePackagesMenuActionsContainerProps) => state.pricePackages.gridData.data[props.index],
    (
        pricePackageIndex: string,
        total: number,
        actions: PricePackagesMenuActions,
        accessRights: AccessRights,
        login: string,
        pricePackage: PricePackageBaseFields
    ): {
        actions: Array<GridActionsMenuItems>;
        events: Record<string, () => void>;
    } => {
        const result = {
            actions: [],
            events: {}
        };

        const accessActions = accessRights.actions;

        if (!accessActions) {
            return result;
        }

        const approveAccess = accessActions.includes(GdOperatorAction.APPROVE_PRICE_PACKAGES);
        const managerAccess = accessActions.includes(GdOperatorAction.MANAGE_PRICE_PACKAGES);

        if (!approveAccess && !managerAccess) {
            return result;
        }

        const items = [];
        let events = {};

        if (approveAccess) {
            items.push(
                {
                    body: i18n('reject-package'),
                    type: pricePackagesAction.rejectPricePackage,
                    disabled: pricePackage.statusApprove !== GdStatusApprove.WAITING
                },
                {
                    body: i18n('approve-package'),
                    type: pricePackagesAction.approvePricePackage,
                    disabled: pricePackage.statusApprove !== GdStatusApprove.WAITING
                }
            );

            events = assign(events, {
                [pricePackagesAction.rejectPricePackage]: (): void => actions.onReject(pricePackageIndex),
                [pricePackagesAction.approvePricePackage]: (): void => actions.onApprove(pricePackageIndex)
            });
        }

        if (managerAccess) {
            items.push(
                {
                    body: i18n('to-approve'),
                    type: pricePackagesAction.sendToApprovePricePackage,
                    disabled: pricePackage.statusApprove !== GdStatusApprove.NEW
                },
                {
                    body: i18n('to-edit'),
                    type: pricePackagesAction.editPricePackage,
                    disabled:
                        pricePackage.statusApprove === GdStatusApprove.NO ||
                        pricePackage.statusApprove === GdStatusApprove.YES
                }
            );

            events = assign(events, {
                [pricePackagesAction.sendToApprovePricePackage]: (): void => actions.onSendToApprove(pricePackageIndex),
                [pricePackagesAction.editPricePackage]: (): void => actions.onEdit(login, pricePackage.id)
            });
        }

        return {
            actions: [
                {
                    items
                }
            ],
            events
        };
    }
)((state: State, props: PricePackagesMenuActionsContainerProps) => props.index);

export const getPricePackagesClientsModalData = createSelector(
    (state: State, id: number) => selectPricePackagesEditor(state, id),
    (pricePackagesData: PricePackageBaseFields): PricePackagesClientsModalState => {
        if (!pricePackagesData) {
            return {
                isVisible: false,
                isProgress: false,
                tags: [],
                title: ''
            };
        }

        return {
            isVisible: true,
            isProgress: false,
            tags: pricePackagesData.clientIds
                ? pricePackagesData.clientIds.map(data => {
                      return {
                          value: data.toString(),
                          type: TargetingTagType.INCLUDED,
                          tagState: TagState.UNSET
                      };
                  })
                : [],
            title: pricePackagesData.title,
            id: pricePackagesData.id,
            lastUpdateTime: pricePackagesData.lastUpdateTime
        };
    }
);

export const selectPricePackagesEditor = (state: State, id: number): PricePackageBaseFields => {
    return state.pricePackages.gridData.data[id];
};
